// Adapted from davinci-elections apps/web/src/components/member/FlowStepper.tsx @ 6e23f58
import { Box, Button, chakra, Flex, Grid, Heading, Stack, Text, VisuallyHidden } from '@chakra-ui/react'
import { createContext, useContext, useLayoutEffect, type ElementType } from 'react'
import { LuArrowLeft, LuIdCard, LuMail, LuMapPin, LuScanFace, LuUser } from 'react-icons/lu'
import type { MouseEvent } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router'
import type { DecoHue } from '~/components/IconTile'
import { useT } from '~/i18n/use-t'
import { useFlowSteps, type StepKey } from '~/lib/steps'
import { NARROW, ROOMY, visuallyHidden } from '~/theme/media'
import { Illustration, type ArtName } from './Illustration'

/** The icon, tint and emblem of each step (the start page's list of steps uses the icon and tint). */
export const STEP_LOOK: Record<StepKey, { icon: ElementType; hue: DecoHue; art: ArtName }> = {
  about: { icon: LuUser, hue: 'green', art: 'about' },
  email: { icon: LuMail, hue: 'blue', art: 'email' },
  place: { icon: LuMapPin, hue: 'peach', art: 'place' },
  document: { icon: LuIdCard, hue: 'peach', art: 'document' },
  face: { icon: LuScanFace, hue: 'green', art: 'face' },
}

/** The registration step a screen shows (FlowStepper reports it), so MemberLayout leaves out its own Back row. */
export const StepContext = createContext<(step: StepKey | null) => void>(() => {})

/**
 * Where "Back" goes on the page (MemberLayout provides it). A step heading shows the Back link itself, in its own top
 * row on a phone, so MemberLayout leaves its own Back row out while a step heading is on the page.
 */
export const BackContext = createContext<{ to: string; history: boolean } | undefined>(undefined)

function useReportStep(step: StepKey) {
  const report = useContext(StepContext)
  useLayoutEffect(() => {
    report(step)
    return () => report(null)
  }, [report, step])
}

/** "Back" at the top left of a member screen (copy v7 "Rules for every screen"). */
export function BackLink({ to, history = false }: { to: string; history?: boolean }) {
  const { t } = useT()
  const navigate = useNavigate()
  // React Router keeps the position in `history.state.idx`: above 0 there is an earlier entry of this app to go back to.
  const canGoBack =
    history && typeof window !== 'undefined' && ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0
  const onClick = (e: MouseEvent) => {
    if (!canGoBack) return
    e.preventDefault()
    void navigate(-1)
  }
  return (
    <Button asChild variant='ghost' px={2} ms={-2} minW='12' color='fg.muted' _hover={{ color: 'fg' }}>
      <RouterLink to={to} onClick={onClick}>
        <LuArrowLeft aria-hidden />
        {/* Under 360 px the arrow alone (the word stays for screen readers), so "Step n of N" keeps one line. */}
        <chakra.span css={{ [NARROW]: visuallyHidden }}>{t('common.back')}</chakra.span>
      </RouterLink>
    </Button>
  )
}

/**
 * The step header (design spec 2026-10-30 §2.2), the same on every width: a row with Back, "Step n of N" over the
 * segmented progress bar (one segment per step of the public policy, the done ones in the accent) and the step's
 * emblem; then the step title. The title is the page's one `h1`, whose name stays "Step n of N: <title>" (the prefix
 * is there for screen readers only). The emblem is 40 px on a phone, 56 px on a tablet or a computer and left out
 * under 500 px tall. `art` replaces the emblem (the waiting screen).
 */
export const FlowStepper = ({ step, art }: { step: StepKey; art?: ArtName }) => {
  const { t } = useT()
  const { total, numberOf } = useFlowSteps()
  const n = numberOf(step)
  const back = useContext(BackContext)
  useReportStep(step)
  const label = t('common.stepNOfM', { n, m: total })
  return (
    <Grid
      data-flow-stepper=''
      alignItems='center'
      columnGap={3}
      rowGap={2}
      css={{
        gridTemplateColumns: back ? 'auto minmax(0, 1fr) auto' : 'minmax(0, 1fr) auto',
        gridTemplateAreas: back ? '"back meta art" "title title title"' : '"meta art" "title title"',
      }}
    >
      {back && (
        <Box gridArea='back'>
          <BackLink to={back.to} history={back.history} />
        </Box>
      )}
      <Stack data-meta='' gridArea='meta' gap={1.5} minW={0} aria-hidden>
        <Text color='fg.muted' lineHeight='1.5' whiteSpace='nowrap' overflow='hidden' textOverflow='ellipsis'>
          {label}
        </Text>
        <Flex gap={1.5}>
          {Array.from({ length: total }, (_, i) => (
            <Box key={i} flex='1' h='4px' borderRadius='full' bg={i < n ? 'brand.solid' : 'bg.emphasized'} />
          ))}
        </Flex>
      </Stack>
      <Illustration gridArea='art' name={art ?? STEP_LOOK[step]!.art} />
      {/* No aria-label: the hidden "Step n of N: " makes the text itself read "Step n of N: <title>" (an aria-label on
          a heading would also make every "label contains <title>" lookup, e.g. a Password field, match the heading). */}
      <Heading
        as='h1'
        gridArea='title'
        minW={0}
        textWrap='balance'
        css={{ fontSize: '2xl', lineHeight: '1.25', [ROOMY]: { fontSize: '1.75rem' } }}
      >
        <VisuallyHidden>{`${label}:`}</VisuallyHidden>
        {` ${t(`steps.${step}`)}`}
      </Heading>
    </Grid>
  )
}
